import { z } from 'zod';

import { localDateTimeSchema } from '@apple/utils/globalization/models';
import { type PaginatedListDto, paginatedListDtoSchema } from '@apple/utils/pagination/models';
import type { Guid } from '@apple/utils/primitive';

import {
	type ProductListingModel,
	productListingModelSchema,
} from '@apple/features/product/models/product';

import { type StoreEventCategory, storeEventCategorySchema } from './categories';

/** @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventItemModel */
export interface StoreEventLine {
	storeEventItemId: Guid | null;
	/** The backend seems not to map this value in the category line endpoint */
	storeEventCategoryId: Guid | null;
	product: ProductListingModel;
	quantity: number;
	isLocked: boolean;
	isOrderLocked: boolean;
	isCancelled: boolean;
	isDisabled: boolean;
	isManualAdd: boolean;
	isAdditionPending: boolean;
	comment: string | null;
	itemLockedDate: Date | string | null;
	canReSync: boolean;
	canBeRemoved: boolean;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemListingRequest */
export interface StoreEventLineFilters {
	storeEventId: Guid;
	currentPage: number;
	pageSize: number;
	excludeZeroQuantityItems: boolean;
	excludeCancelledItems: boolean;
	categoryIds: string[];
	onlyManualAdditions: boolean;
	onlyBackOrderedItem: boolean;
	hasOnlyLockedItems: boolean;
	searchTerm: string;
}

export const DEFAULT_STORE_EVENT_LINE_FILTERS: Omit<StoreEventLineFilters, 'storeEventId'> = {
	currentPage: 1,
	pageSize: 99999,
	excludeZeroQuantityItems: false,
	excludeCancelledItems: true,
	categoryIds: [],
	onlyManualAdditions: false,
	onlyBackOrderedItem: false,
	hasOnlyLockedItems: false,
	searchTerm: '',
};

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemListResponse */
export interface StoreEventLinesResponse {
	items: StoreEventLine[];
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemGroupModel */
export interface CategoryLines {
	category: StoreEventCategory;
	items: StoreEventLine[];
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemGroupListModel */
export interface CategoryLinesPaginatedList extends PaginatedListDto<CategoryLines> {
	categoryCount: number;
	nonZeroQuantityItems: number;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemUpdateModel */
export interface LineUpdate {
	storeEventItemId: Guid | null;
	storeEventCategoryId: Guid;
	productItemId: string;
	quantity: number;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemUpdateRequest */
export interface LineUpdateRequest {
	storeEventId: Guid;
	updates: LineUpdate[];
	comment: string;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemCommentModel */
export interface LineComment {
	storeEventItemId: Guid;
	comment: string;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemCommentUpdateRequest */
export interface LineCommentUpdates {
	commentUpdates: LineComment[];
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemErpDetailModel */
export interface StoreEventItemErpDetail {
	storeEventItemId: Guid;
	allocatedQuantity: number;
	pickedQuantity: number;
	shippedQuantity: number;
	availableQuantity: number;
	demandQuantity: number;
	purchaseOrderNumber: string | null;
	externalLineItemKey: string | null;
	nextPoDate: Date | string | null;
}

/** @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventItemModel */
export const storeEventLineSchema = z.object({
	storeEventItemId: z.string().nullable(),
	storeEventCategoryId: z.string().nullable(),
	product: productListingModelSchema,
	quantity: z.number(),
	isLocked: z.boolean(),
	isOrderLocked: z.boolean(),
	isCancelled: z.boolean(),
	isDisabled: z.boolean(),
	isManualAdd: z.boolean(),
	isAdditionPending: z.boolean(),
	comment: z.string().nullable(),
	itemLockedDate: localDateTimeSchema.nullable(),
	canReSync: z.boolean(),
	canBeRemoved: z.boolean(),
}) satisfies z.ZodType<StoreEventLine>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemListingRequest */
export const storeEventLineFiltersSchema = z.object({
	storeEventId: z.string(),
	currentPage: z.number(),
	pageSize: z.number(),
	excludeZeroQuantityItems: z.boolean(),
	excludeCancelledItems: z.boolean(),
	categoryIds: z.array(z.string()),
	onlyManualAdditions: z.boolean(),
	onlyBackOrderedItem: z.boolean(),
	hasOnlyLockedItems: z.boolean(),
	searchTerm: z.string(),
}) satisfies z.ZodType<StoreEventLineFilters>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemGroupModel */
export const categoryLinesSchema = z.object({
	category: storeEventCategorySchema,
	items: z.array(storeEventLineSchema),
}) satisfies z.ZodType<CategoryLines>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemGroupListModel */
export const categoryLinesPaginatedListSchema = paginatedListDtoSchema(categoryLinesSchema).extend({
	categoryCount: z.number(),
	nonZeroQuantityItems: z.number(),
}) satisfies z.ZodType<CategoryLinesPaginatedList>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemListResponse */
export const storeEventLineResponseSchema = z.object({
	items: z.array(storeEventLineSchema),
}) satisfies z.ZodType<StoreEventLinesResponse>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemUpdateModel */
export const lineUpdateSchema = z.object({
	storeEventItemId: z.string().nullable(),
	storeEventCategoryId: z.string(),
	productItemId: z.string(),
	quantity: z.number(),
}) satisfies z.ZodType<LineUpdate>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemQuantityApiController.StoreEventItemUpdateRequest */
export const lineUpdateRequestSchema = z.object({
	storeEventId: z.string(),
	updates: z.array(lineUpdateSchema),
	comment: z.string(),
}) satisfies z.ZodType<LineUpdateRequest>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemCommentModel */
export const lineCommentSchema = z.object({
	storeEventItemId: z.string(),
	comment: z.string(),
}) satisfies z.ZodType<LineComment>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemCommentUpdateRequest */
export const lineCommentUpdatesSchema = z.object({
	commentUpdates: z.array(lineCommentSchema),
}) satisfies z.ZodType<LineCommentUpdates>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventItemDetailApiController.StoreEventItemErpDetailModel */
export const storeEventItemErpDetailSchema = z.object({
	storeEventItemId: z.string(),
	allocatedQuantity: z.number(),
	pickedQuantity: z.number(),
	shippedQuantity: z.number(),
	availableQuantity: z.number(),
	demandQuantity: z.number(),
	nextPoDate: localDateTimeSchema.nullable(),
	purchaseOrderNumber: z.string().nullable(),
	externalLineItemKey: z.string().nullable(),
}) satisfies z.ZodType<StoreEventItemErpDetail>;
export const storeEventItemErpDetailListSchema = z.array(storeEventItemErpDetailSchema);
